import { useId } from "react";
import { CalendarDays, ChevronRight, CircleAlert, MessageSquare, Wrench } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { serviceCategorySlug } from "../lib/serviceCategoryIcons";
import { dayjs } from "../lib/time";

const COPY = {
  de: {
    heading: "Nächstes & Versorgung", appointment: "Nächster Termin", services: "Services", requests: "Anfragen",
    noAppointment: "Kein weiterer Termin geplant.", noServices: "Keine kommenden Services", noRequests: "Keine offenen Anfragen",
    unavailable: "Daten nicht verfügbar", timeUnavailable: "Terminzeit nicht verfügbar", careUnavailable: "Care-Status nicht verfügbar",
    nextServiceUnavailable: "Nächster Service nicht verfügbar", reasonUnavailable: "Anlass nicht verfügbar",
    openAppointment: "Termin öffnen", openCalendar: "Kalender öffnen", openService: "Service öffnen", allServices: "Alle Services",
    openRequest: "Anfrage öffnen", allRequests: "Alle Anfragen", service: "Service", genericAppointment: "Termin",
    next: "Nächster Service", actionRequired: "Aktion erforderlich", waitingForPlayer: "Wartet auf Spieler",
    noCareAction: "Aktuell keine Aktion erforderlich",
    upcoming: (count) => `${count} kommende${count === 1 ? "r Service" : " Services"}`,
    servicesNeedCare: (count) => `${count} ${count === 1 ? "Service" : "Services"} · Aktion erforderlich`,
    openRequests: (count) => `${count} offen`, requestsNeedCare: (count) => `${count} ${count === 1 ? "wartet" : "warten"} auf Care`,
    reasons: {
      decision_required: "Entscheidung erforderlich", care_response_required: "Antwort von Care ausstehend",
      care_task_required: "Care-Aufgabe offen", player_response_required: "Antwort des Spielers ausstehend", open_request: "Anfrage offen",
    },
    priorities: { high: "Hohe Priorität", urgent: "Dringend", critical: "Kritisch" },
    categories: {
      training: "Training", workout: "Training", match: "Spiel", game: "Spiel", recovery: "Recovery", physio: "Physiotherapie",
      cleaning: "Reinigung", "housing-moving": "Wohnen / Umzug", vehicle: "Fahrzeug", "travel-transfer": "Reise / Transfer",
      reservation: "Reservierung", "shopping-errand": "Einkauf / Besorgung", "repair-trades": "Reparatur / Handwerker",
      "documents-organization": "Dokumente / Organisation", other: "Sonstiger Service", service: "Service", appointment: "Termin",
    },
  },
  en: {
    heading: "Up next & care", appointment: "Next appointment", services: "Services", requests: "Requests",
    noAppointment: "No further appointment planned.", noServices: "No upcoming services", noRequests: "No open requests",
    unavailable: "Data unavailable", timeUnavailable: "Appointment time unavailable", careUnavailable: "Care status unavailable",
    nextServiceUnavailable: "Next service unavailable", reasonUnavailable: "Reason unavailable",
    openAppointment: "Open appointment", openCalendar: "Open calendar", openService: "Open service", allServices: "All services",
    openRequest: "Open request", allRequests: "All requests", service: "Service", genericAppointment: "Appointment",
    next: "Next service", actionRequired: "Action required", waitingForPlayer: "Waiting for player",
    noCareAction: "No action currently required",
    upcoming: (count) => `${count} upcoming ${count === 1 ? "service" : "services"}`,
    servicesNeedCare: (count) => `${count} ${count === 1 ? "service" : "services"} · Action required`,
    openRequests: (count) => `${count} open`, requestsNeedCare: (count) => `${count} ${count === 1 ? "needs" : "need"} Care`,
    reasons: {
      decision_required: "Decision required", care_response_required: "Care reply pending",
      care_task_required: "Care task open", player_response_required: "Player reply pending", open_request: "Request open",
    },
    priorities: { high: "High priority", urgent: "Urgent", critical: "Critical" },
    categories: {
      training: "Training", workout: "Training", match: "Match", game: "Match", recovery: "Recovery", physio: "Physiotherapy",
      cleaning: "Cleaning", "housing-moving": "Housing / moving", vehicle: "Vehicle", "travel-transfer": "Travel / transfer",
      reservation: "Reservation", "shopping-errand": "Shopping / errands", "repair-trades": "Repairs / trades",
      "documents-organization": "Documents / organization", other: "Other service", service: "Service", appointment: "Appointment",
    },
  },
};

const countOrNull = (value) => Number.isInteger(value) && value >= 0 ? value : null;

function categoryLabel(category, copy, fallback) {
  const slug = serviceCategorySlug({ category_id: category }) || serviceCategorySlug(category);
  return copy.categories[slug] || fallback;
}

function appointmentTime(value, timezone, lang) {
  if (!value) return null;
  try {
    const date = dayjs.utc(value).tz(timezone || "UTC").locale(lang);
    if (!date.isValid()) return null;
    return { text: date.format("DD MMM YYYY · HH:mm"), date: date.format("YYYY-MM-DD"), timezone: timezone || "UTC" };
  } catch {
    return null;
  }
}

function Time({ value, formatted, copy }) {
  return formatted
    ? <time className="care-cockpit-supply-time" dateTime={value} title={formatted.timezone}
      aria-label={`${formatted.text} (${formatted.timezone})`}>{formatted.text}</time>
    : <span className="care-cockpit-supply-secondary">{copy.timeUnavailable}</span>;
}

function OpenButton({ onOpen, target, label, children, testId }) {
  return <button type="button" className={`care-cockpit-supply-link${children ? " care-cockpit-supply-link--detail" : ""}`}
    disabled={typeof onOpen !== "function"} onClick={() => onOpen(target)} data-testid={testId}>
    <span className="care-cockpit-supply-link-body">
      {children}
      <span className="care-cockpit-supply-link-action">{label}</span>
    </span>
    <ChevronRight size={16} aria-hidden="true" />
  </button>;
}

function CareStatus({ children }) {
  return <span className="care-cockpit-supply-status care-cockpit-supply-status--attention">
    <CircleAlert size={14} aria-hidden="true" />{children}
  </span>;
}

export default function CareCockpitSupply({ cockpit, timezone, onOpen }) {
  const { lang } = useI18n();
  const copy = COPY[lang] || COPY.en;
  const id = useId();
  const appointment = cockpit?.next_appointment;
  const services = cockpit?.services;
  const requests = cockpit?.requests;
  const nextService = services?.next_service;
  const serviceShownAsAppointment = appointment?.source === "service"
    && Boolean(appointment.service_id) && appointment.service_id === nextService?.service_id;
  const highestRequest = requests?.highest_priority_request;
  const upcomingCount = countOrNull(services?.upcoming_count);
  const serviceCareCount = countOrNull(services?.care_action_required_count);
  const openRequestCount = countOrNull(requests?.open_request_count);
  const requestCareCount = countOrNull(requests?.care_action_required_count);
  const nextTime = appointmentTime(appointment?.start_at, timezone, lang);
  const serviceTime = appointmentTime(nextService?.start_at, timezone, lang);
  const appointmentTitle = appointment?.title || categoryLabel(appointment?.category, copy,
    appointment?.source === "service" ? copy.service : copy.genericAppointment);
  const requestReason = copy.reasons[highestRequest?.reason] || copy.reasonUnavailable;
  const priority = copy.priorities[highestRequest?.priority];
  // A null projection is a known empty result; an omitted projection is unavailable.
  // Keep server IDs and the player's local day intact for the parent's scoped navigation.
  const appointmentTarget = appointment?.source === "service"
    ? { view: "services", ...(appointment.service_id ? { serviceId: appointment.service_id } : {}) }
    : { tab: "calendar", ...(appointment?.schedule_item_id ? { scheduleItemId: appointment.schedule_item_id } : {}),
      ...(appointment?.local_date || nextTime?.date ? { date: appointment?.local_date || nextTime.date } : {}) };

  return <aside className="care-cockpit-supply" aria-labelledby={`${id}-heading`} data-testid="care-cockpit-supply">
    <h2 className="care-cockpit-supply-heading" id={`${id}-heading`}>{copy.heading}</h2>

    <section className="care-cockpit-aside-section care-cockpit-aside-section--next" aria-labelledby={`${id}-next`}
      data-testid="care-cockpit-next-appointment">
      <h3 className="care-cockpit-supply-section-heading" id={`${id}-next`}><CalendarDays size={17} aria-hidden="true" />{copy.appointment}</h3>
      {appointment ? <OpenButton onOpen={onOpen} target={appointmentTarget} label={copy.openAppointment} testId="care-cockpit-open-appointment">
        <strong className="care-cockpit-supply-link-title">{appointmentTitle}</strong>
        <Time value={appointment.start_at} formatted={nextTime} copy={copy} />
      </OpenButton> : <>
        <p className="care-cockpit-supply-secondary">{appointment === null ? copy.noAppointment : copy.unavailable}</p>
        <OpenButton onOpen={onOpen} target={{ tab: "calendar" }} label={copy.openCalendar} />
      </>}
    </section>

    <section className="care-cockpit-aside-section care-cockpit-aside-section--services" aria-labelledby={`${id}-services`}
      data-testid="care-cockpit-services">
      <h3 className="care-cockpit-supply-section-heading" id={`${id}-services`}><Wrench size={17} aria-hidden="true" />{copy.services}</h3>
      <p className="care-cockpit-supply-summary">{upcomingCount === null ? copy.unavailable : upcomingCount === 0 ? copy.noServices : copy.upcoming(upcomingCount)}</p>
      {serviceCareCount > 0 && <CareStatus>{copy.servicesNeedCare(serviceCareCount)}</CareStatus>}
      {services && serviceCareCount === null && <p className="care-cockpit-supply-secondary">{copy.careUnavailable}</p>}
      {nextService && !serviceShownAsAppointment ? <OpenButton onOpen={onOpen}
        target={{ view: "services", ...(nextService.service_id ? { serviceId: nextService.service_id } : {}) }}
        label={copy.openService} testId="care-cockpit-open-service">
        <span className="care-cockpit-supply-eyebrow">{copy.next}</span>
        <strong className="care-cockpit-supply-link-title">{categoryLabel(nextService.category, copy, copy.service)}</strong>
        <Time value={nextService.start_at} formatted={serviceTime} copy={copy} />
        {nextService.care_action_required === true ? <CareStatus>{copy.actionRequired}</CareStatus>
          : nextService.next_actor === "player" ? <span className="care-cockpit-supply-secondary">{copy.waitingForPlayer}</span> : null}
      </OpenButton> : !nextService && upcomingCount > 0 ? <p className="care-cockpit-supply-secondary">{copy.nextServiceUnavailable}</p> : null}
      <OpenButton onOpen={onOpen} target={{ view: "services" }} label={copy.allServices} />
    </section>

    <section className="care-cockpit-aside-section care-cockpit-aside-section--requests" aria-labelledby={`${id}-requests`}
      data-testid="care-cockpit-requests">
      <h3 className="care-cockpit-supply-section-heading" id={`${id}-requests`}><MessageSquare size={17} aria-hidden="true" />{copy.requests}</h3>
      <p className="care-cockpit-supply-summary">{openRequestCount === null ? copy.unavailable : openRequestCount === 0 ? copy.noRequests : copy.openRequests(openRequestCount)}</p>
      {requestCareCount > 0 ? <CareStatus>{copy.requestsNeedCare(requestCareCount)}</CareStatus>
        : requestCareCount === 0 && openRequestCount > 0 ? <p className="care-cockpit-supply-secondary">{copy.noCareAction}</p>
          : requests && requestCareCount === null ? <p className="care-cockpit-supply-secondary">{copy.careUnavailable}</p> : null}
      {highestRequest && <OpenButton onOpen={onOpen}
        target={{ tab: "requests", ...(highestRequest.request_id ? { requestId: highestRequest.request_id } : {}) }}
        label={copy.openRequest} testId="care-cockpit-open-request">
        <strong className="care-cockpit-supply-link-title">{requestReason}</strong>
        {priority && <span className={`care-cockpit-supply-status care-cockpit-supply-status--${["urgent", "critical"].includes(highestRequest.priority) ? "critical" : "attention"}`}>{priority}</span>}
      </OpenButton>}
      <OpenButton onOpen={onOpen} target={{ tab: "requests" }} label={copy.allRequests} />
    </section>
  </aside>;
}
